import { action, extendObservable, reaction } from 'mobx';
import { PropTypes as MobxPropTypes } from 'mobx-react';
import PropTypes from 'prop-types';
import FlagStore from '../FlagStore';
import InputStore from '../InputStore';
import MenuStore from '../MenuStore';
import urlPatternValidator from '../../urlPatternValidator';
import debug from '../../debug';
import UrlBulkEditorCardStore from '../UrlBulkEditorCardStore';

const sortPredicate = (a, b) => {
    if (a.name < b.name) {
        return -1;
    }
    if (a.name > b.name) {
        return 1;
    }
    return 0;
};

export class CategoryFilterStore {
    static PropType = PropTypes.shape({
        categorySearchStore: InputStore.PropType,

        getAllowedCount: PropTypes.func,
        getBlockedCount: PropTypes.func,
        getFilteredAllowedCategories: PropTypes.func,
        getFilteredBlockedCategories: PropTypes.func,
        isNotNamedSetCategory: PropTypes.func,
        blockAll: PropTypes.func,
        allowAll: PropTypes.func,
    });

    constructor(data = {}) {
        extendObservable(this, /** @class CategoryFilterStore */{
            allCategories: [],
            forbiddenCategories: [],
            originalForbiddenCategories: [],

            categorySearchStore: new InputStore(),
            searchedDomainCategoryIds: [],

            getAllowedCount() {
                return this.getAllowedCategories().length;
            },

            getBlockedCount() {
                return this.getBlockedCategories().length;
            },

            getAllowedCategories() {
                return this.allCategories.filter((cat) => this.forbiddenCategories.indexOf(cat.id) === -1);
            },

            getBlockedCategories() {
                return this.allCategories.filter((cat) => this.forbiddenCategories.indexOf(cat.id) !== -1);
            },

            getFilteredAllowedCategories() {
                let retVal = this.getAllowedCategories();
                const searchText = this.categorySearchStore.value.trim().toLowerCase();

                if (searchText) {
                    retVal = retVal.filter(({ id, name }) => {
                        return this.searchedDomainCategoryIds.length > 0 ?
                            this.searchedDomainCategoryIds.includes(id) : name.toLowerCase().includes(searchText);
                    });
                }

                retVal.sort(sortPredicate);
                return retVal;
            },

            getFilteredBlockedCategories() {
                let retVal = this.getBlockedCategories();
                const searchText = this.categorySearchStore.value.trim().toLowerCase();

                if (searchText) {
                    retVal = retVal.filter(({ id, name }) => {
                        return this.searchedDomainCategoryIds.length > 0 ?
                            this.searchedDomainCategoryIds.includes(id) : name.toLowerCase().includes(searchText);
                    });
                }

                retVal.sort(sortPredicate);
                return retVal;
            },

            get hasChanged() {
                return (this.forbiddenCategories.length !== this.originalForbiddenCategories.length)
                    || (!this.originalForbiddenCategories.every((i) => {
                        return this.forbiddenCategories.includes(i);
                    }));
            },
        }, data);
    }

    isNotNamedSetCategory = (c) => c.id < 1001;

    setFromObject = action((data) => {
        this.allCategories.replace((data.allCategories || []).filter(this.isNotNamedSetCategory));
        this.forbiddenCategories.replace(data.forbiddenCategories || []);
        this.originalForbiddenCategories.replace(data.forbiddenCategories || []);
        this.categorySearchStore.setValue('');
        this.searchedDomainCategoryIds.clear();
    });

    toObject = () => ({
        forbiddenCategories: this.forbiddenCategories,
    });

    blockAll = action(() => {
        this.forbiddenCategories.push(...this.getFilteredAllowedCategories().map((cat) => cat.id));
    });

    allowAll = action(() => {
        const filteredIds = this.getFilteredBlockedCategories().map((cat) => cat.id);
        const newForbiddenCategories = this.forbiddenCategories.filter((cat) => filteredIds.indexOf(cat) === -1);
        this.forbiddenCategories.replace(newForbiddenCategories);
    });
}

export class UrlListStore {
    static PropType = PropTypes.shape({
        filteredList: MobxPropTypes.arrayOrObservableArray,

        menu: MenuStore.PropType.isRequired,
        modalTextField: InputStore.PropType,
        modal: FlagStore.PropType,
        urlGuidelines: FlagStore.PropType,
        isModalEditing: PropTypes.bool,
        unmodifiedUrl: PropTypes.string,

        toggleMenu: PropTypes.func,
        setFilterCriteria: PropTypes.func,
        editInModal: PropTypes.func,
        addNewUrl: PropTypes.func,
        canSubmit: PropTypes.func,
        virtualListKey: PropTypes.string,
    });

    constructor(data = {}) {
        extendObservable(this, /** @class UrlListStore */{
            list: [],
            originalListSorted: [],
            filteredList: [],
            filter: '',

            menu: new MenuStore(),
            modal: new FlagStore(),
            editedItemIdx: null,
            modalTextField: new InputStore(),
            urlGuidelines: new FlagStore(),
            virtualListKey: '_',

            get isModalEditing() {
                return (this.editedItemIdx !== null) && Number.isSafeInteger(this.editedItemIdx);
            },

            hasChanged() {
                return (this.list.length !== this.originalListSorted.length)
                    || ![...this.list].sort().every((value, index) => this.originalListSorted[index] === value);
            },

            canSubmit() {
                return !this.modalTextField.isEmpty && (this.modalTextField.isTouched || this.modalTextField.isValid);
            },
        }, data);

        this.modalTextField.setValidator(urlPatternValidator);

        reaction(() => this.filter, this.setFilteredList);
    }

    setFilteredList = action((filter) => {
        filter = (filter || '').trim().toLowerCase();
        this.filteredList.replace(filter
            ? this.list.filter((s) => s.toLowerCase().includes(filter))
            : this.list);
    });

    setValue = action((list) => {
        this.list.replace(list);
        this.originalListSorted.replace([...list].sort());
        this.setFilteredList(this.filter);
    });

    getValue = () => [...this.list];

    setFilter = action((value) => {
        this.filter = value;
    });

    removeFromList = action((url) => {
        if (this.list.remove(url)) {
            this.setFilteredList(this.filter);
            this.menu.close();
        }
    });

    addNewUrl = action(() => {
        this.modal.show();
        this.editedItemIdx = null;
        this.modalTextField.setValue('');
    });

    editInModal = action((url) => {
        this.modal.show();
        this.menu.close();
        this.editedItemIdx = this.list.indexOf(url);
        this.modalTextField.setValue(url);
    });

    saveModalChanges = action(() => {
        this.modalTextField.setTouched(false);

        if (!this.modalTextField.isValid) {
            this.modalTextField.focus();
            return;
        }

        if (this.isModalEditing) {
            this.list[this.editedItemIdx] = this.modalTextField.value;
            this.virtualListKey = (new Date()).toISOString();
        } else {
            this.list.push(this.modalTextField.value);
        }
        this.setFilteredList(this.filter);
        this.modal.close();
    });
}
export class URLFilterStore {
    static PropType = PropTypes.shape({
        allowedUrl: UrlBulkEditorCardStore.PropType,
        blockedUrl: UrlBulkEditorCardStore.PropType,
    });

    constructor(data = {}) {
        extendObservable(this, /** @class URLFilterStore */{
            allowedUrl: new UrlBulkEditorCardStore(),
            blockedUrl: new UrlBulkEditorCardStore(),

            hasChanged: () => {
                return this.allowedUrl.hasChanged()
                    || this.blockedUrl.hasChanged();
            },
            get isValid() {
                return this.allowedUrl.isValid && this.blockedUrl.isValid;
            },
        }, data);
    }

    setFromObject = action((data) => {
        this.allowedUrl.setValue(data.allowedUrls);
        this.blockedUrl.setValue(data.blockedUrls);
    });

    toObject = () => ({
        blacklistRules: this.blockedUrl.getValue(),
        whitelistRules: this.allowedUrl.getValue(),
    });
}

export class ContentFilteringStore {
    static PropType = PropTypes.shape({
        filterContentSwitchStore: FlagStore.PropType.isRequired,
        categoryFilterStore: CategoryFilterStore.PropType.isRequired,
        urlFilterStore: URLFilterStore.PropType.isRequired,
        whitelistThreatSimStore: FlagStore.PropType.isRequired,
    });

    constructor(data = {}) {
        extendObservable(this, /** @class ContentFilteringStore */{
            filterContentSwitch: false,
            filterContentSwitchStore: new FlagStore(false),
            categoryFilterStore: new CategoryFilterStore(),
            urlFilterStore: new URLFilterStore(),
            whitelistThreatSim: true,
            whitelistThreatSimStore: new FlagStore(true),

            hasChanged: () => {
                return (this.filterContentSwitchStore.value !== this.filterContentSwitch)
                    || this.categoryFilterStore.hasChanged
                    || this.urlFilterStore.hasChanged()
                    || (this.whitelistThreatSimStore.value !== this.whitelistThreatSim);
            },
        }, data);
    }

    setFromObject = action((data) => {
        this.filterContentSwitch = data.filterContent;
        this.filterContentSwitchStore.setValue(data.filterContent);
        this.whitelistThreatSim = data.whitelistThreatSim;
        this.whitelistThreatSimStore.setValue(data.whitelistThreatSim);
        this.categoryFilterStore.setFromObject(data);
        if (data.webFilterList) {
            // TODO: use same field names
            this.urlFilterStore.setFromObject({
                blockedUrls: data.webFilterList.blacklistRules,
                allowedUrls: data.webFilterList.whitelistRules,
            });
        }
    });

    toObject = () => ({
        filterContent: this.filterContentSwitchStore.value,
        whitelistThreatSim: this.whitelistThreatSimStore.value,
        webFilterList: this.urlFilterStore.toObject(),
        ...this.categoryFilterStore.toObject(),
    });
}
